import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';

export type WebHookId = string;

export const webHookTypes = ['snapshot', 'intent-verification'] as const;
export type WebHookType = (typeof webHookTypes)[number];

export type WebHookAction =
  | 'discover'
  | 'clone'
  | 'delete'
  | 'download'
  | 'load'
  | 'unload'
  | 'calculate';

// Persisted trigger condition (the `trigger` JSONB column). Mirrors the API
// shape; the form represents these as a `Set<string>` of TreePicker leaf keys.
export interface WebhookTrigger {
  type: WebHookType;
  action?: string;
  status?: string;
}

// Persisted payload template (the `payloadTemplate` JSONB column). `template` is
// a mustache string; `format` drives the editor's syntax highlighting.
export interface WebhookPayloadTemplate {
  format: 'json' | 'xml';
  template: string;
}

export interface WebHookRowData {
  active?: boolean;
  id: WebHookId;
  name: string;
  secret: string;
  sendAll: boolean;
  types: WebHookType[];
  trigger: WebhookTrigger[] | null;
  payloadTemplate?: WebhookPayloadTemplate | null;
  // Persisted custom headers (the `headerTemplate` JSONB column): header name →
  // mustache value template.
  headerTemplate?: Record<string, string> | null;
  // Write-only outbound auth token: the API never returns its value, only
  // whether one is configured.
  hasToken?: boolean;
  caCertId: string | null;
  url: string;
  username: string;
}

export interface EventType {
  description: {
    actions: WebHookAction[];
    general: string;
    payload: string;
  };
  type: WebHookType;
}

export type DeliveryId = string;

export interface Delivery {
  id: DeliveryId;
  error?: string;
  inProgress: boolean;
  retry: number; // order of this retry
  timestamp: Date;
  url: string;
  request: {
    body: {
      timestamp: number;
      type: WebHookType;
    };
    hash: string;
  };
  response?: {
    headers: Record<string, string>; // http request headers
    status: number; // http status code
    statusText: string;
  };
  vWebhookSettingsId: string;
}

export interface WebHooksState {
  dataFetching: boolean;
  deliveriesFetching: boolean;
  eventTypes: EventType[];
  recentDeliveries: Delivery[] | null;
  webhooks: WebHookRowData[];
  caCerts: { id: string; name: string }[];
  savedSecret: string | null;
  detailLoadingState: LoadingState;
}

export const initialWebHooksState: WebHooksState = {
  dataFetching: true,
  deliveriesFetching: false,
  eventTypes: [],
  recentDeliveries: null,
  webhooks: [],
  caCerts: [],
  savedSecret: null,
  detailLoadingState: 'INITIAL',
};
